import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE } from "../../../../config";
import RenderBlocks, { GCSE_STANDARDS } from "../../../RenderBlocks";
import { renderToString } from "katex";
import "katex/dist/katex.min.css";
import renderMathInElement from "katex/contrib/auto-render";

// --- Validation for question object ---
const validateQuestionObject = (q) => {
  if (typeof q !== "object" || q === null) return "Item is not an object.";
  const required = ["question_text", "explanation", "question_type", "correct_answer", "order_number"];
  for (const key of required) {
    if (!(key in q)) return `Missing required field: "${key}".`;
  }
  if (q.options != null && !Array.isArray(q.options) && q.question_type?.toLowerCase() === "mcq") {
    return `"options" must be an array for MCQ questions.`;
  }
  if (q.render_blocks != null && !Array.isArray(q.render_blocks)) {
    return `"render_blocks" must be an array (or omit it).`;
  }
  return null;
};

const EXAMPLE_TEMPLATES = {
  rightTriangle: {
    name: "Right Triangle (Trigonometry)",
    json: [
      {
        "question_text": "In the right-angled triangle ABC, angle BAC = 37°. Calculate the length of side BC. Give your answer to 2 decimal places.",
        "explanation": "Using trigonometry: $\\tan(37°) = \\frac{BC}{AB} = \\frac{BC}{4}$. Therefore $BC = 4 \\times \\tan(37°) = 4 \\times 0.7536 \\approx 3.01$ cm",
        "question_type": "short_answer",
        "correct_answer": "3.01",
        "options": null,
        "render_blocks": [
          {
            "type": "graph",
            "data": {
              "xRange": [-1, 6],
              "yRange": [-1, 5],
              "showGrid": false,
              "showAxes": false,
              "points": [
                { "x": 0, "y": 0, "label": "A", "labelQuadrant": "bottomLeft", "color": "#000000" },
                { "x": 4, "y": 0, "label": "B", "labelQuadrant": "bottomRight", "color": "#000000" },
                { "x": 4, "y": 3, "label": "C", "labelQuadrant": "topRight", "color": "#000000" }
              ],
              "lines": [
                { 
                  "start": [0, 0], 
                  "end": [4, 0], 
                  "label": "4 cm",
                  "labelSide": "below",
                  "rotateLabel": false,
                  "color": "#000000",
                  "strokeWidth": "2"
                },
                { 
                  "start": [4, 0], 
                  "end": [4, 3], 
                  "label": "?",
                  "labelSide": "right",
                  "rotateLabel": false,
                  "color": "#000000",
                  "strokeWidth": "2"
                },
                { 
                  "start": [0, 0], 
                  "end": [4, 3], 
                  "label": "5 cm",
                  "labelSide": "above",
                  "rotateLabel": true,
                  "color": "#000000",
                  "strokeWidth": "2"
                }
              ],
              "rightAngleMarkers": [
                {
                  "vertex": [4, 0],
                  "point1": [0, 0],
                  "point2": [4, 3],
                  "color": "#000000"
                }
              ],
              "angleArcs": [
                {
                  "center": [0, 0],
                  "points": [[4, 0], [4, 3]],
                  "label": "37°",
                  "color": "#000000"
                }
              ]
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },
  
  coordinateGeometry: {
    name: "Coordinate Geometry",
    json: [
      {
        "question_text": "What is the gradient of the line shown in the graph?",
        "explanation": "The line passes through (0, 1) and (2, 5). Gradient = $\\frac{y_2 - y_1}{x_2 - x_1} = \\frac{5-1}{2-0} = \\frac{4}{2} = 2$",
        "question_type": "short_answer",
        "correct_answer": "2",
        "options": null,
        "render_blocks": [
          {
            "type": "graph",
            "data": {
              "xRange": [-3, 5],
              "yRange": [-2, 7],
              "gridSize": 1,
              "showGrid": true,
              "showAxes": true,
              "functions": [
                {
                  "expression": "2*x + 1",
                  "color": "#000000",
                  "strokeWidth": 2
                }
              ]
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },

  parallelLines: {
    name: "Parallel Lines & Angles",
    json: [
      {
        "question_text": "In the diagram, AB is parallel to CD. Calculate the value of angle x.",
        "explanation": "AB and CD are parallel lines with a transversal. The angle marked 65° and angle x are alternate angles, so they are equal. Therefore x = 65°",
        "question_type": "mcq",
        "correct_answer": "b) 65°",
        "options": [
          "a) 115°",
          "b) 65°",
          "c) 25°",
          "d) 155°"
        ],
        "render_blocks": [
          {
            "type": "graph",
            "data": {
              "xRange": [-1, 10],
              "yRange": [-1, 6],
              "showGrid": false,
              "showAxes": false,
              "points": [
                { "x": 0, "y": 4, "label": "A", "labelQuadrant": "left", "color": "#000000" },
                { "x": 9, "y": 4, "label": "B", "labelQuadrant": "right", "color": "#000000" },
                { "x": 0, "y": 1, "label": "C", "labelQuadrant": "left", "color": "#000000" },
                { "x": 9, "y": 1, "label": "D", "labelQuadrant": "right", "color": "#000000" }
              ],
             "lines": [
  { 
    "start": [0, 4], 
    "end": [9, 4],
    "color": "#000000",
    "strokeWidth": "2"
  },
  { 
    "start": [0, 1], 
    "end": [9, 1],
    "color": "#000000",
    "strokeWidth": "2"
  },
  { 
    "start": [3, 5], 
    "end": [6, 0],
    "color": "#000000",
    "strokeWidth": "2"
  },
  // Parallel marks on LEFT side of lines (away from angle arcs)
  { 
    "start": [0.5, 4], 
    "end": [2.5, 4],
    "parallelMarks": 2,
    "color": "#000000",
    "strokeWidth": "2"
  },
  { 
    "start": [0.5, 1], 
    "end": [2.5, 1],
    "parallelMarks": 2,
    "color": "#000000",
    "strokeWidth": "2"
  }
],
              "angleArcs": [
                {
                  "center": [3.6, 4],
                  "points": [[4.6, 4], [4.6, 2.3]],
                  "label": "65",
                  "color": "#000000",
                  "radius": 30
                },
                {
                  "center": [5.4, 1],
                  "points": [[4.4, 2.7], [4.4, 1]],
                  "label": "x",
                  "color": "#000000",
                  "radius": 30
                }
              ]
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },

  triangleAngles: {
    name: "Triangle Angles",
    json: [
      {
        "question_text": "Calculate the missing angle x in the triangle:",
        "explanation": "The sum of angles in a triangle is 180°. So x = 180° - 56° - 68° = 56°",
        "question_type": "short_answer",
        "correct_answer": "56",
        "options": null,
        "render_blocks": [
          {
            "type": "geometry",
            "data": {
              "vertices": [[0, 0], [5, 0], [2.5, 4]],
              "labels": ["A", "B", "C"],
              "labelPositions": ["bottomLeft", "bottomRight", "top"],
              "showAngles": true,
              "angles": [56, 68, "x"],
              "showMeasurements": false,
              "style": {
                "stroke": "#000000",
                "fill": "none",
                "strokeWidth": "2"
              }
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },

  numberLineInequality: {
    name: "Number Line Inequality",
    json: [
      {
        "question_text": "Which inequality is represented on the number line?",
        "explanation": "The open circle at -2 with shading to the right represents x > -2 (x is greater than -2 but not equal to -2)",
        "question_type": "mcq",
        "correct_answer": "b) x > -2",
        "options": [
          "a) x < -2",
          "b) x > -2", 
          "c) x ≥ -2",
          "d) x ≤ -2"
        ],
        "render_blocks": [
          {
            "type": "numberLine",
            "data": {
              "min": -5,
              "max": 5,
              "step": 1,
              "inequalities": [
                {
                  "value": -2,
                  "type": "open",
                  "direction": "right",
                  "color": "#000000"
                }
              ]
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },

  circleTheorem: {
    name: "Circle Theorem",
    json: [
      {
        "question_text": "A circle has center O and radius 5 cm. Find the area of the sector with angle 60°. Use $\\pi = 3.14$",
        "explanation": "Area of sector = $\\frac{\\theta}{360} \\times \\pi r^2 = \\frac{60}{360} \\times 3.14 \\times 5^2 = \\frac{1}{6} \\times 3.14 \\times 25 \\approx 13.08$ cm²",
        "question_type": "short_answer",
        "correct_answer": "13.08",
        "options": null,
        "render_blocks": [
          {
            "type": "graph",
            "data": {
              "xRange": [-1, 11],
              "yRange": [-1, 11],
              "showGrid": false,
              "showAxes": false,
              "shapes": [
                {
                  "type": "circle",
                  "center": [5, 5],
                  "radius": 5,
                  "fill": "none",
                  "stroke": "#000000",
                  "strokeWidth": "2"
                }
              ],
              "lines": [
                {
                  "start": [5, 5],
                  "end": [10, 5],
                  "stroke": "#000000",
                  "strokeWidth": "2",
                  "label": "5 cm",
                  "labelSide": "below"
                },
                {
                  "start": [5, 5],
                  "end": [7.5, 9.33],
                  "stroke": "#000000",
                  "strokeWidth": "2"
                }
              ],
              "points": [
                { "x": 5, "y": 5, "label": "O", "labelQuadrant": "bottomLeft", "size": 3, "color": "#000000" },
                { "x": 10, "y": 5, "size": 3, "color": "#000000", "filled": true },
                { "x": 7.5, "y": 9.33, "size": 3, "color": "#000000", "filled": true }
              ],
              "angleArcs": [
                {
                  "center": [5, 5],
                  "points": [[10, 5], [7.5, 9.33]],
                  "label": "60°",
                  "color": "#000000"
                }
              ]
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },

  quadraticGraph: {
    name: "Quadratic Function",
    json: [
      {
        "question_text": "The graph shows $y = x^2 - 4x + 3$. What are the coordinates of the minimum point?",
        "explanation": "The minimum point of the parabola is at (2, -1). This can be found by completing the square: $y = (x-2)^2 - 1$, so the vertex is at (2, -1)",
        "question_type": "short_answer",
        "correct_answer": "(2, -1)",
        "options": null,
        "render_blocks": [
          {
            "type": "graph",
            "data": {
              "xRange": [-1, 5],
              "yRange": [-2, 6],
              "gridSize": 1,
              "showGrid": true,
              "showAxes": true,
              "functions": [
                {
                  "expression": "x^2 - 4*x + 3",
                  "color": "#000000",
                  "strokeWidth": 2
                }
              ]
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },
  
  barChart: {
    name: "Bar Chart (Statistics)",
    json: [
      {
        "question_text": "The bar chart shows the number of pets owned by students in a class. How many students own exactly 2 pets?",
        "explanation": "Looking at the bar chart, the bar for '2 pets' shows a frequency of 8 students",
        "question_type": "short_answer",
        "correct_answer": "8",
        "options": null,
        "render_blocks": [
          {
            "type": "diagram",
            "data": {
              "type": "bar",
              "data": [5, 8, 12, 7, 3],
              "labels": ["0", "1", "2", "3", "4+"],
              "xLabel": "Number of pets",
              "yLabel": "Frequency",
              "title": "Pets Owned by Students"
            }
          }
        ],
        "modality": "visual",
        "is_seed": false,
        "requires_visual": true,
        "order_number": 1
      }
    ]
  },

  algebraEquation: {
    name: "Algebra (Text-based)",
    json: [
      {
        "question_text": "Solve the quadratic equation: $3x^2 - 12x + 9 = 0$",
        "explanation": "Factor out 3: $3(x^2 - 4x + 3) = 0$. Then factor: $3(x-1)(x-3) = 0$. So x = 1 or x = 3",
        "question_type": "short_answer",
        "correct_answer": "1, 3",
        "options": null,
        "modality": "text",
        "is_seed": false,
        "requires_visual": false,
        "order_number": 1
      }
    ]
  }
};

const AdminQuiz = ({ topicId, onClose }) => {
  // Data
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [error, setError] = useState(null);

  // Editor state
  const [jsonText, setJsonText] = useState("");
  const [editQuestion, setEditQuestion] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  // Parse + validate JSON in real time
  const { parsed, parseError, validationErrors } = useMemo(() => {
    if (!jsonText.trim()) return { parsed: null, parseError: null, validationErrors: [] };
    try {
      const data = JSON.parse(jsonText);
      if (!Array.isArray(data)) {
        return { parsed: null, parseError: "Top-level JSON must be an array of question objects.", validationErrors: [] };
      }
      const errs = [];
      data.forEach((q, idx) => {
        const e = validateQuestionObject(q);
        if (e) errs.push(`Item ${idx + 1}: ${e}`);
      });
      return { parsed: data, parseError: null, validationErrors: errs };
    } catch (e) {
      return { parsed: null, parseError: e.message || "Invalid JSON.", validationErrors: [] };
    }
  }, [jsonText]);

  // Fetch existing questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/questions/topic/${topicId}`);
        setQuestions(response.data || []);
      } catch (err) {
        console.error("Error fetching questions:", err);
        setError("Failed to fetch questions.");
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [topicId]);

  // Handlers
  const handleInputChange = (e, index = null, isEditing = false) => {
    const { name, value } = e.target;
    if (!isEditing) return;

    setEditQuestion((prev) => {
      if (!prev) return prev;
      if (name === "options") {
        const updatedOptions = Array.isArray(prev.options) ? [...prev.options] : [];
        updatedOptions[index] = value;
        return { ...prev, options: updatedOptions };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleCreateFromJson = async () => {
    if (!parsed || parseError || validationErrors.length) {
      toast.error("Please fix JSON errors before creating questions.");
      return;
    }

    setLoadingCreate(true);
    try {
      const response = await axios.post(`${API_BASE}/api/questions/create-from-json/${topicId}`, {
        jsonText,
        questions: parsed,
      });

      // Refresh list
      const questionsResponse = await axios.get(`${API_BASE}/api/questions/topic/${topicId}`);
      setQuestions(questionsResponse.data || []);

      // Clear editor
      setJsonText("");
      setSelectedTemplate("");

      const createdCount = Array.isArray(response.data?.questions)
        ? response.data.questions.length
        : (Array.isArray(response.data) ? response.data.length : parsed.length);

      toast.success(`Successfully created ${createdCount} question${createdCount === 1 ? "" : "s"}!`);
    } catch (err) {
      console.error("Error creating questions from JSON:", err);
      toast.error("Failed to create questions. Please check your format.");
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editQuestion) return;
    try {
      const response = await axios.put(`${API_BASE}/api/questions/${editQuestion.id}`, editQuestion);
      setQuestions((prev) => prev.map((q) => (q.id === editQuestion.id ? response.data : q)));
      setEditQuestion(null);
      toast.success("Question updated successfully!");
    } catch (err) {
      console.error("Error updating question:", err);
      toast.error("Failed to update question.");
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await axios.delete(`${API_BASE}/api/questions/${questionId}`);
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
      toast.success("Question deleted successfully!");
    } catch (err) {
      console.error("Error deleting question:", err);
      toast.error("Failed to delete question.");
    }
  };

  const loadTemplate = (templateKey) => {
    if (templateKey && EXAMPLE_TEMPLATES[templateKey]) {
      setJsonText(JSON.stringify(EXAMPLE_TEMPLATES[templateKey].json, null, 2));
      setSelectedTemplate(templateKey);
    }
  };

  const copyJsonToClipboard = () => {
    if (!parsed || parseError || validationErrors.length) {
      toast.error("Please fix JSON errors first.");
      return;
    }
    const previewQuestions = parsed.map(q => ({ ...q, topic_id: topicId }));
    navigator.clipboard.writeText(JSON.stringify(previewQuestions, null, 2))
      .then(() => toast.success("JSON copied to clipboard!"))
      .catch(() => toast.error("Failed to copy to clipboard"));
  };

  const exportQuestionsAsJson = () => {
    const exportData = questions.map(q => {
      const { id, created_at, updated_at, topic_id, ...rest } = q;
      return rest;
    });
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `topic_${topicId}_questions.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Zoom controls
  const handleZoomIn = () => setPreviewZoom(prev => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setPreviewZoom(prev => Math.max(prev - 0.1, 0.5));
  const handleZoomReset = () => setPreviewZoom(1);

  // Helpers for rendering
  const normalizeLatexText = (text) => {
    if (!text || typeof text !== "string") return text || "";
    let s = text;
    s = s.replace(/\\\$begin:math:text\$(.*?)\\\$end:math:text\$/g, "$1");
    s = s.replace(/(^|[^\\])(tfrac)(\s*\{)/g, "$1\\$2$3");
    s = s.replace(/(^|[^\\])(frac)(\s*\{)/g, "$1\\$2$3");
    s = s.replace(/(^|[^\\])(sqrt)(\s*\{)/g, "$1\\$2$3");
    s = s.replace(/\\\\/g, "\\");
    return s;
  };

  const renderOptionHtml = (option) => {
    const s = typeof option === "string" ? option : String(option ?? "");
    const match = s.match(/^([a-d]\)\s*)([\s\S]*)$/i);
    const prefix = match ? match[1] : "";
    const content = normalizeLatexText(match ? match[2] : s);
    try {
      const html = renderToString(content, { throwOnError: false });
      return { prefix, html };
    } catch {
      return { prefix, html: content };
    }
  };

  const formatAnswerForDisplay = (answer) => {
    if (!answer) return 'Not Available';
    const normalized = normalizeLatexText(answer);
    if (/\\[a-zA-Z]|\^|_/g.test(normalized)) {
      if (normalized.includes('\\(') && normalized.includes('\\)')) {
        return normalized;
      }
      return `\$begin:math:text$${normalized}\\$end:math:text$`;
    }
    return normalized;
  };

  // Auto-render LaTeX
  const previewContainerRef = useRef(null);
  useEffect(() => {
    try {
      if (showPreview && previewContainerRef.current) {
        renderMathInElement(previewContainerRef.current, {
          delimiters: [
            { left: "$", right: "$", display: false },
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true }
          ],
          throwOnError: false
        });
      }
    } catch {}
  }, [showPreview, parsed]);

  const listContainerRef = useRef(null);
  useEffect(() => {
    try {
      if (listContainerRef.current) {
        renderMathInElement(listContainerRef.current, {
          delimiters: [
            { left: "$", right: "$", display: false },
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true }
          ],
          throwOnError: false
        });
      }
    } catch {}
  }, [questions, editQuestion]);

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-lg p-8 w-11/12 max-w-7xl overflow-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-teal-600">
            Manage Quiz Questions (Topic ID: {topicId})
          </h2>
          <button onClick={onClose} className="text-red-600 hover:text-red-700 text-2xl">✖</button>
        </div>

        {/* Create from JSON Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-teal-600 mb-3">Create Questions from JSON</h3>

          {/* Template Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Load Example Template:
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(EXAMPLE_TEMPLATES).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => loadTemplate(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedTemplate === key
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mb-4 flex gap-2 flex-wrap">
            <button
              onClick={copyJsonToClipboard}
              disabled={!parsed || !!parseError || validationErrors.length > 0}
              className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              📋 Copy JSON
            </button>
            <button
              onClick={exportQuestionsAsJson}
              disabled={questions.length === 0}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              💾 Export Existing
            </button>
            <button
              onClick={() => setJsonText("")}
              disabled={!jsonText}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🗑️ Clear Editor
            </button>
          </div>

          {/* Documentation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">📚 RenderBlocks Format Reference</h4>
            <div className="text-xs text-blue-800 space-y-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <strong>Graphs:</strong> <code className="bg-white px-1 rounded">type: "graph"</code>
                  <ul className="ml-4 mt-1 text-xs">
                    <li>• Functions, points, lines, shapes</li>
                    <li>• Angle arcs, inequalities</li>
                    <li>• Smart label positioning</li>
                  </ul>
                </div>
                <div>
                  <strong>Geometry:</strong> <code className="bg-white px-1 rounded">type: "geometry"</code>
                  <ul className="ml-4 mt-1 text-xs">
                    <li>• Triangles, polygons, circles</li>
                    <li>• Transformations, sectors</li>
                    <li>• Angle and side labels</li>
                  </ul>
                </div>
                <div>
                  <strong>Number Lines:</strong> <code className="bg-white px-1 rounded">type: "numberLine"</code>
                  <ul className="ml-4 mt-1 text-xs">
                    <li>• Intervals and inequalities</li>
                    <li>• Open/closed circles</li>
                  </ul>
                </div>
                <div>
                  <strong>Diagrams:</strong> <code className="bg-white px-1 rounded">type: "diagram"</code>
                  <ul className="ml-4 mt-1 text-xs">
                    <li>• Bar, pie, histogram charts</li>
                    <li>• Venn diagrams, trees</li>
                  </ul>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-blue-200">
                <strong>New Features:</strong> Right angle markers, parallel line marks, auto-rotating labels, standardized spacing
              </div>
            </div>
          </div>

          {/* JSON Editor */}
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='Paste or edit JSON here, or load a template above...'
            className="w-full h-64 border-2 border-gray-300 rounded-lg px-4 py-3 font-mono text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" }}
          />

          {/* Validation Panel */}
          <div className="mt-3">
            {parseError && (
              <div className="bg-red-100 border border-red-400 text-red-700 p-3 rounded-lg mb-2">
                <strong>❌ JSON Parse Error:</strong> {parseError}
              </div>
            )}
            {!parseError && validationErrors.length > 0 && (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 p-3 rounded-lg mb-2">
                <strong>⚠️ Validation Issues:</strong>
                <ul className="list-disc ml-6 mt-1">
                  {validationErrors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            {!parseError && parsed && validationErrors.length === 0 && (
              <div className="bg-green-100 border border-green-400 text-green-800 p-3 rounded-lg mb-2">
                ✅ Valid JSON. Detected <strong>{parsed.length}</strong> question{parsed.length === 1 ? "" : "s"}.
              </div>
            )}
          </div>

          {/* Preview Section */}
          {parsed && !parseError && validationErrors.length === 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-md font-semibold text-gray-700">👁️ Question Preview</h4>
                <div className="flex items-center gap-2">
                  {showPreview && (
                    <div className="flex items-center gap-2 mr-4">
                      <button
                        onClick={handleZoomOut}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm font-medium"
                        title="Zoom Out"
                      >
                        −
                      </button>
                      <span className="text-sm text-gray-600 min-w-[3.5rem] text-center font-medium">
                        {Math.round(previewZoom * 100)}%
                      </span>
                      <button
                        onClick={handleZoomIn}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm font-medium"
                        title="Zoom In"
                      >
                        +
                      </button>
                      <button
                        onClick={handleZoomReset}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded text-sm ml-2"
                        title="Reset Zoom"
                      >
                        Reset
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 text-sm font-medium"
                  >
                    {showPreview ? "Hide Preview" : "Show Preview"}
                  </button>
                </div>
              </div>
              
              {showPreview && (
                <div 
                  ref={previewContainerRef} 
                  className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-300 rounded-lg p-6 overflow-auto shadow-inner"
                  style={{ 
                    maxHeight: '600px',
                    transform: `scale(${previewZoom})`,
                    transformOrigin: 'top left',
                    width: `${100 / previewZoom}%`
                  }}
                >
                  <div className="space-y-6">
                    {parsed.map((question, index) => (
                      <div 
                        key={index} 
                        className="bg-white border-2 border-teal-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-gray-200">
                          <span className="text-lg font-bold text-teal-700">
                            Question {question.order_number || index + 1}
                          </span>
                          <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                            {question.question_type?.toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Question Text */}
                        <div className="mb-5">
                          <h5 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Question:</h5>
                          <div className="text-gray-900 leading-relaxed text-base bg-gray-50 p-4 rounded-lg border border-gray-200">
                            {normalizeLatexText(question.question_text || "")}
                          </div>
                        </div>

                        {/* Render Blocks */}
                        {question.render_blocks && question.render_blocks.length > 0 && (
                          <div className="mb-5">
                            <h5 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                              Visual Elements:
                            </h5>
                            <div className="border-2 border-gray-300 rounded-lg bg-white p-5 shadow-sm">
                              <RenderBlocks blocks={question.render_blocks} />
                            </div>
                          </div>
                        )}

                        {/* Options (MCQ) */}
                        {question.options && Array.isArray(question.options) && (
                          <div className="mb-5">
                            <h5 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Options:</h5>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {question.options.map((option, optIndex) => {
                                const { prefix, html } = renderOptionHtml(option);
                                return (
                                  <div 
                                    key={optIndex} 
                                    className="bg-gray-100 hover:bg-gray-200 p-3 rounded-lg border-2 border-gray-300 transition-colors cursor-pointer"
                                  >
                                    <span className="font-semibold text-gray-700">{prefix}</span>
                                    <span dangerouslySetInnerHTML={{ __html: html }} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Correct Answer */}
                        <div className="mb-5">
                          <h5 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Correct Answer:</h5>
                          <div
                            className="inline-block bg-green-100 text-green-900 px-4 py-2 rounded-lg font-semibold border-2 border-green-300"
                            dangerouslySetInnerHTML={{ __html: formatAnswerForDisplay(question.correct_answer) }}
                          />
                        </div>

                        {/* Explanation */}
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Explanation:</h5>
                          <div className="text-sm text-gray-700 leading-relaxed bg-blue-50 p-4 rounded-lg border border-blue-200">
                            {normalizeLatexText(question.explanation || "")}
                          </div>
                        </div>

                        {/* Metadata Tags */}
                        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                          {question.modality && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-medium">
                              📊 {question.modality}
                            </span>
                          )}
                          {question.is_seed && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-medium">
                              🌱 Seed Question
                            </span>
                          )}
                          {question.requires_visual && (
                            <span className="text-xs bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-medium">
                              👁️ Visual Required
                            </span>
                          )}
                          <span className="text-xs bg-gray-200 text-gray-800 px-3 py-1 rounded-full font-medium">
                            #️⃣ Order: {question.order_number}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Create Button */}
          <div className="mt-4">
            <button
              onClick={handleCreateFromJson}
              disabled={loadingCreate || !parsed || !!parseError || validationErrors.length > 0}
              className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
            >
              {loadingCreate ? "Creating..." : `✨ Create ${parsed?.length || 0} Question${parsed?.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>

        {/* Existing Questions List */}
        <div className="border-t-2 border-gray-300 pt-6">
          <h3 className="text-lg font-semibold text-teal-600 mb-3">
            📚 Existing Questions ({questions.length})
          </h3>
          <div className="overflow-y-auto max-h-96 mb-6" ref={listContainerRef}>
            {loading ? (
              <p className="text-gray-500">Loading questions...</p>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : questions.length === 0 ? (
              <p className="text-gray-500">No questions available. Create some using the JSON editor above.</p>
            ) : (
              questions.map((question) => (
                <div key={question.id} className="bg-gray-50 border border-gray-300 p-5 rounded-lg mb-3 shadow hover:shadow-md transition-shadow">
                  {editQuestion?.id === question.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-gray-700">Question Text:</label>
                      <textarea
                        name="question_text"
                        value={editQuestion.question_text || ""}
                        onChange={(e) => handleInputChange(e, null, true)}
                        className="border-2 rounded-lg px-4 py-2 w-full h-24 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                      />

                      {editQuestion.question_type === "mcq" && (
                        <>
                          <label className="text-sm font-semibold text-gray-700">Options:</label>
                          <div className="grid grid-cols-2 gap-2">
                            {Array.isArray(editQuestion.options) ? (
                              editQuestion.options.map((option, index) => (
                                <input
                                  key={index}
                                  type="text"
                                  name="options"
                                  value={option}
                                  placeholder={`Option ${index + 1}`}
                                  onChange={(e) => handleInputChange(e, index, true)}
                                  className="border-2 rounded-lg px-4 py-2 w-full focus:border-teal-500"
                                />
                              ))
                            ) : (
                              <p className="text-gray-500 col-span-2">No options (numeric/open question)</p>
                            )}
                          </div>
                        </>
                      )}

                      <label className="text-sm font-semibold text-gray-700">Correct Answer:</label>
                      <input
                        type="text"
                        name="correct_answer"
                        value={editQuestion.correct_answer || ""}
                        onChange={(e) => handleInputChange(e, null, true)}
                        placeholder="Correct Answer"
                        className="border-2 rounded-lg px-4 py-2 w-full focus:border-teal-500"
                      />

                      <label className="text-sm font-semibold text-gray-700">Explanation:</label>
                      <textarea
                        name="explanation"
                        value={editQuestion.explanation || ""}
                        onChange={(e) => handleInputChange(e, null, true)}
                        placeholder="Explanation"
                        className="border-2 rounded-lg px-4 py-2 w-full h-24 focus:border-teal-500"
                      />

                      <label className="text-sm font-semibold text-gray-700">Render Blocks (JSON):</label>
                      <textarea
                        name="render_blocks"
                        value={JSON.stringify(editQuestion.render_blocks || [], null, 2)}
                        onChange={(e) => {
                          try {
                            const blocks = JSON.parse(e.target.value);
                            if (Array.isArray(blocks)) {
                              setEditQuestion(prev => ({ ...prev, render_blocks: blocks }));
                            }
                          } catch {
                            // ignore invalid JSON
                          }
                        }}
                        className="border-2 rounded-lg px-4 py-2 w-full h-40 font-mono text-sm focus:border-teal-500"
                      />

                      {/* Live preview */}
                      {Array.isArray(editQuestion.render_blocks) && editQuestion.render_blocks.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Live Preview:</p>
                          <div className="border-2 border-gray-300 rounded-lg bg-white p-4">
                            <RenderBlocks blocks={editQuestion.render_blocks} />
                          </div>
                        </div>
                      )}

                      <div className="flex space-x-3 pt-3">
                        <button
                          onClick={handleUpdateQuestion}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-semibold"
                        >
                          ✓ Update
                        </button>
                        <button
                          onClick={() => setEditQuestion(null)}
                          className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 font-semibold"
                        >
                          ✗ Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Read-only View
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <p className="font-bold text-gray-900 flex-1 text-lg">{question.question_text}</p>
                        <span className="text-xs bg-gray-200 text-gray-700 px-3 py-1 rounded-full ml-3 font-medium">
                          ID: {question.id}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm mb-3 bg-white p-3 rounded border border-gray-200">
                        <div>
                          <p><span className="font-semibold">Type:</span> {question.question_type}</p>
                          <p><span className="font-semibold">Answer:</span> {question.correct_answer}</p>
                          <p><span className="font-semibold">Order:</span> {question.order_number}</p>
                        </div>
                        <div>
                          <p><span className="font-semibold">Modality:</span> {question.modality || "text"}</p>
                          <p><span className="font-semibold">Visual:</span> {question.requires_visual ? "Yes" : "No"}</p>
                          <p><span className="font-semibold">Seed:</span> {question.is_seed ? "Yes" : "No"}</p>
                        </div>
                      </div>

                      {Array.isArray(question.options) && question.options.length > 0 && (
                        <div className="mt-3">
                          <span className="font-semibold text-sm">Options:</span>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {question.options.map((opt, i) => (
                              <span key={i} className="text-sm bg-gray-200 px-3 py-2 rounded border border-gray-300">
                                {opt}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <p className="mt-3 text-sm bg-blue-50 p-3 rounded border border-blue-200">
                        <span className="font-semibold">Explanation:</span> {question.explanation}
                      </p>

                      {/* Visual elements preview */}
                      {Array.isArray(question.render_blocks) && question.render_blocks.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-semibold mb-2">
                            Visual Elements: {question.render_blocks.map(b => b.type).join(", ")}
                          </p>
                          <div className="border-2 border-teal-200 rounded-lg bg-white p-4">
                            <RenderBlocks blocks={question.render_blocks} />
                          </div>
                        </div>
                      )}

                      <div className="flex space-x-3 mt-4 pt-3 border-t border-gray-300">
                        <button
                          onClick={() => setEditQuestion(question)}
                          className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 font-semibold"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm("Are you sure you want to delete this question?")) {
                              handleDeleteQuestion(question.id);
                            }
                          }}
                          className="bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 font-semibold"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuiz;